import { useForm } from '@tanstack/react-form';
import { HelpCircle } from '@untitled-ui/icons-react';
import { useEffect } from 'react';
import { z } from 'zod';

import { Button } from '../../../../../../components/buttons/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '../../../../../../components/dialog';
import { TextField } from '../../../../../../components/inputs/text-field';
import { Tooltip } from '../../../../../../components/tooltip';
import { tGlobal, tNamespace } from '../../../../../../services/translation';
import { parseFirstZodError } from '../../../../../../shared/validation';

const t = tNamespace('app.integrations');

export type EditIntegrationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (integrationName: string, integrationId: string) => void;
  integrationName: string;
  integrationId: string;
  isLoading?: boolean;
};

export function EditIntegrationDialog(props: EditIntegrationDialogProps) {
  const { open, onOpenChange, onSubmit, integrationName, integrationId, isLoading } = props;

  const form = useForm({
    defaultValues: {
      integrationName,
    },
    onSubmit: (values) => {
      onSubmit(values.value.integrationName, integrationId);
    },
  });

  useEffect(
    function () {
      form.reset(open ? { integrationName } : undefined);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, integrationName]
  );

  function handleClose() {
    form.reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={isLoading ? undefined : handleClose}>
      <DialogContent hasCloseButton={!isLoading}>
        <DialogTitle align="center">{t('editIntegrationName')}</DialogTitle>
        <form id="edit-integration-form" className="mt-8">
          <form.Field
            name="integrationName"
            validators={{
              onChange: function (data) {
                return parseFirstZodError(z.string().min(1), data.value);
              },
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  label={
                    <div className="flex items-center gap-2">
                      <p>{t('integrationName')}</p>
                      <Tooltip text={t('integrationNameTooltip')}>
                        <div>
                          <HelpCircle width={12} height={12} />
                        </div>
                      </Tooltip>
                    </div>
                  }
                  placeholder={t('integrationNamePlaceholder')}
                  sizeField="md"
                  className="w-full"
                  value={field.state.value}
                  onChange={function (e) {
                    field.handleChange(e.target.value);
                  }}
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
        </form>
        <DialogFooter>
          <Button
            size="xl"
            variant="outline"
            className="w-full"
            disabled={isLoading}
            onClick={handleClose}
          >
            {tGlobal('cancel')}
          </Button>
          <Button
            size="xl"
            variant="accent"
            className="w-full"
            loading={isLoading}
            onClick={function () {
              void form.handleSubmit();
            }}
          >
            <p className="whitespace-nowrap">{tGlobal('save')}</p>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
